/**
 * Single source of truth for column-width limits shared by TableCore and
 * ReportsTableWithData.
 *
 * PM-18398 requires one configurable minimum that is applied consistently by
 * every resize path (drag, keyboard, double-click auto-fit) and by the
 * persistence logic that restores saved widths. No maximum is enforced.
 */

// Minimum width a column can be resized to, by any means.
export const MIN_COLUMN_WIDTH = 30;

// Floor applied when computing adaptive *default* widths for a column that the
// user has never resized. Deliberately larger than MIN_COLUMN_WIDTH: it governs
// the initial layout, not what the user is allowed to drag to.
export const MIN_ADAPTIVE_COLUMN_WIDTH = 80;

// Container width below which the table stops laying columns out side by side
// and stacks each row instead (PM-18150 edge: "columns stack or collapse as
// needed for mobile usability"; collapse confirmed by Naveen 2026-09-11).
// 768px is the breakpoint this repo already uses most often, and matches
// Bootstrap 4's `md` that reactstrap follows.
export const COLLAPSE_BREAKPOINT = 768;

// Upper clamp on a column's content-derived natural width. Acts as the
// tie-breaker PM-18386 asks for, so one very long value cannot swallow the
// whole table.
export const MAX_NATURAL_COLUMN_WIDTH = 360;

// Applied to every column via react-table's `defaultColumn` option, so the
// library clamps drag-resize and restored widths to the same minimum.
export const DEFAULT_COLUMN_SETTINGS = {
    minWidth: MIN_COLUMN_WIDTH,
};

/** Clamp saved/restored widths to the shared minimum (PM-18398, persistence). */
export const clampWidths = (widths) => {
    const clamped = {};
    Object.entries(widths || {}).forEach(([id, width]) => {
        if (typeof width === 'number' && Number.isFinite(width)) {
            clamped[id] = Math.max(Math.round(width), MIN_COLUMN_WIDTH);
        }
    });
    return clamped;
};

// Columns whose cells hold interactive controls rather than text. They must
// never be wrapped in the truncating cell: an ellipsis is meaningless for a row
// of buttons, and `overflow: hidden` clipped the second action button and the
// "..." overflow menu out of reach entirely (PM-21741).
//
// Both spellings are required: DevicesTable, ReportsTable and EmergencyInfoTable
// use `action`, while UsersTable uses `actions`.
export const NON_TRUNCATING_COLUMNS = new Set([
    'action',
    'actions',
    'selection',
    'expander',
]);
